import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, STAFF_ROLES } from "@/context/AuthContext";
import { Logo, DemoBadge } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, User, ClipboardList, Users, Database, Settings,
  Image, Trash2, ScrollText, LogOut, UserCog, CalendarDays, Mail, GraduationCap, HardHat,
} from "lucide-react";

const candidateNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/profile", label: "My Details", icon: User, id: "nav-profile" },
  { to: "/assessment", label: "Assessment", icon: ClipboardList, id: "nav-assessment" },
];

const traineeNav = [
  { to: "/dashboard", label: "My Journey", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/training", label: "Training Camp", icon: GraduationCap, id: "nav-training" },
  { to: "/profile", label: "My Details", icon: User, id: "nav-profile" },
];

const operatorNav = [
  { to: "/dashboard", label: "My Journey", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/development", label: "My Development", icon: HardHat, id: "nav-development" },
  { to: "/training", label: "Training Camp", icon: GraduationCap, id: "nav-training" },
  { to: "/profile", label: "My Details", icon: User, id: "nav-profile" },
];

function staffNav(role) {
  const items = [
    { to: "/staff", label: "Candidates", icon: Users, id: "nav-staff-candidates", end: true },
  ];
  if (role === "management_admin" || role === "instructor" || role === "funding_admin") {
    items.push({ to: "/staff/calendar", label: "Assessment Calendar", icon: CalendarDays, id: "nav-calendar" });
    items.push({ to: "/staff/training", label: "Training Camp", icon: GraduationCap, id: "nav-training-camp" });
    items.push({ to: "/staff/operators", label: "Operators", icon: HardHat, id: "nav-operators" });
  }
  if (role === "management_admin" || role === "instructor") {
    items.push({ to: "/staff/questions", label: "Question Bank", icon: Database, id: "nav-question-bank" });
  }
  if (role === "management_admin") {
    items.push(
      { to: "/staff/templates", label: "Templates", icon: Mail, id: "nav-templates" },
      { to: "/staff/settings", label: "Settings", icon: Settings, id: "nav-settings" },
      { to: "/staff/branding", label: "Branding", icon: Image, id: "nav-branding" },
      { to: "/staff/staff-users", label: "Staff", icon: UserCog, id: "nav-staff-users" },
      { to: "/staff/demo-data", label: "Demo Data", icon: Trash2, id: "nav-demo-data" },
      { to: "/staff/audit", label: "Audit Log", icon: ScrollText, id: "nav-audit" },
    );
  }
  return items;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isStaff = STAFF_ROLES.includes(user?.role);
  const nav = isStaff ? staffNav(user?.role) : (user?.role === "applicant" ? candidateNav : user?.role === "operator" ? operatorNav : traineeNav);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-obsidian text-foreground" data-testid="app-layout">
      <header className="sticky top-0 z-40 bg-white border-b border-steel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <NavLink to={isStaff ? "/staff" : "/dashboard"} data-testid="header-home-link">
            <Logo />
          </NavLink>
          <div className="flex items-center gap-3">
            {user?.is_demo && <DemoBadge />}
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-charcoal leading-tight" data-testid="header-user-name">
                {user?.first_name} {user?.surname}
              </p>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono" data-testid="header-user-role">
                {isStaff ? user?.role?.replace(/_/g, " ") : user?.role}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button"
              className="border-steel text-slate-600 hover:text-lomond hover:border-lomond bg-transparent">
              <LogOut className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto flex">
        <aside className="hidden md:block w-56 shrink-0 border-r border-steel min-h-[calc(100vh-4rem)] py-6 pr-4 pl-4 sm:pl-6">
          <nav className="space-y-1" data-testid="side-nav">
            {nav.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.id}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium border-l-4 transition-colors ${
                    isActive ? "border-lomond bg-lomond/15 text-charcoal font-bold" : "border-transparent text-slate-600 hover:bg-elevated hover:text-charcoal"
                  }`
                }>
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="flex-1 px-4 sm:px-6 py-6 pb-24 md:pb-10 min-w-0">
          <Outlet />
        </main>
      </div>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-slate-panel border-t border-steel flex overflow-x-auto" data-testid="mobile-nav">
        {nav.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} data-testid={`mobile-${item.id}`}
            className={({ isActive }) =>
              `flex-1 min-w-12 shrink-0 flex flex-col items-center gap-1 py-2.5 px-1 text-[10px] font-medium uppercase tracking-wide transition-colors ${
                isActive ? "text-lomond" : "text-slate-500"
              }`
            }>
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
